import { cn } from '@goaltree/ui/lib/utils'
import { Check, ChevronRight } from 'lucide-react'
import { type ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LinkifiedText } from '@/components/linkified-text'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { useStickyHeightVar, useStuck } from '@/hooks/use-stuck'
import { taskTimeLabel } from '@/lib/task-time'

// Shared pieces of the goal plan layout (milestone chapters → optional target
// labels → task rows). The owner tree adds editing/menus on top; the share page,
// templates and draft previews render it read-only via <PlanOutline>.

export type MilestoneState = 'done' | 'current' | 'upcoming'

// Done when every task is complete; current = first unfinished; rest upcoming.
export function milestoneStates(milestones: { tasks: { done: boolean }[] }[]): MilestoneState[] {
  const done = milestones.map((m) => m.tasks.length > 0 && m.tasks.every((t) => t.done))
  const currentIdx = done.indexOf(false)
  return done.map((d, i) => (d ? 'done' : i === currentIdx ? 'current' : 'upcoming'))
}

export function MilestoneEyebrow({ index, count }: { index: number; count: number }) {
  const { t } = useTranslation()
  return (
    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
      {t('goals.milestoneOf', {
        defaultValue: 'Milestone {{n}} of {{total}}',
        n: index + 1,
        total: count,
      })}
    </span>
  )
}

export function MilestonePill({ state }: { state: MilestoneState }) {
  const { t } = useTranslation()
  const label =
    state === 'current'
      ? t('goals.stateCurrent', { defaultValue: 'Current' })
      : state === 'done'
        ? t('goals.done')
        : t('goals.stateUpcoming', { defaultValue: 'Upcoming' })
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
        state === 'current' && 'bg-primary text-primary-foreground',
        state === 'done' && 'border border-primary/40 text-primary-text',
        state === 'upcoming' && 'border border-border text-muted-foreground',
      )}
    >
      {label}
    </span>
  )
}

export function MilestoneProgress({ done, total }: { done: number; total: number }) {
  const pct = total ? Math.round((done / total) * 100) : 0
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
        {done}/{total}
      </span>
    </div>
  )
}

export function FoldedMilestone({
  index,
  count,
  title,
  done,
  total,
  onClick,
  id,
  className,
}: {
  index: number
  count: number
  title: string
  done: number
  total: number
  onClick: () => void
  id?: string
  className?: string
}) {
  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-xl border border-border/60 px-4 py-3 text-left transition-colors hover:border-border',
        className,
      )}
    >
      <TaskStatusIcon status="done" size={18} />
      <span className="grid min-w-0 flex-1 gap-0.5">
        <MilestoneEyebrow index={index} count={count} />
        <span className="truncate text-sm font-semibold text-muted-foreground">{title}</span>
      </span>
      <span className="shrink-0 font-mono text-xs tabular-nums text-primary-text">
        {done}/{total}
      </span>
    </button>
  )
}

export function TargetHeading({
  title,
  count,
  onClick,
}: {
  title: ReactNode
  count: string
  onClick?: () => void
}) {
  return (
    <div
      className={cn('flex items-baseline gap-2 pt-3 pb-0.5', onClick && 'cursor-pointer')}
      onClick={onClick}
    >
      <div className="min-w-0 flex-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
        {title}
      </div>
      <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/60">
        {count}
      </span>
    </div>
  )
}

export function TaskMeta({ children }: { children: ReactNode }) {
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 font-mono text-[11px] text-muted-foreground/60">
      {children}
    </div>
  )
}

export function PlanTaskRow({
  done,
  title,
  meta,
  detail,
  wrap = false,
  onClick,
}: {
  // undefined = a draft task (no completion state yet).
  done?: boolean
  title: ReactNode
  meta?: ReactNode
  detail?: ReactNode
  wrap?: boolean
  onClick?: () => void
}) {
  return (
    <li
      className={cn(
        'flex gap-3 border-b border-border/30 py-1.5 last:border-b-0',
        wrap ? 'items-start' : 'items-center',
        onClick && 'cursor-pointer',
      )}
      onClick={onClick}
    >
      <span className={cn('shrink-0 text-foreground', wrap && 'mt-px')}>
        <TaskStatusIcon status={done ? 'done' : 'pending'} size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'text-sm font-medium',
            wrap ? 'break-words' : 'truncate',
            done && 'text-muted-foreground/60 line-through',
          )}
        >
          {title}
        </div>
        {detail}
      </div>
      {meta && (
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground/60">{meta}</span>
      )}
    </li>
  )
}

type OutlineTask = {
  key: string
  title: string
  description?: string
  effort?: string
  minutes?: number | null
  done?: boolean
}
type OutlineTarget = { key: string; title: string; tasks: OutlineTask[] }
type OutlineMilestone = { key: string; title: string; targets: OutlineTarget[] }

// Read-only plan. `tracked` = tasks carry completion (share page) → state pills,
// progress bars and folded finished milestones; otherwise a draft/template view.
// `sticky` pins milestone and target headers under the app header, like the owner tree.
export function PlanOutline({
  milestones,
  tracked = false,
  sticky = false,
}: {
  milestones: OutlineMilestone[]
  tracked?: boolean
  sticky?: boolean
}) {
  const [reopened, setReopened] = useState<Set<string>>(new Set())
  const toggle = (key: string, open: boolean) =>
    setReopened((prev) => {
      const next = new Set(prev)
      if (open) next.add(key)
      else next.delete(key)
      return next
    })
  const states = milestoneStates(
    milestones.map((m) => ({
      tasks: m.targets.flatMap((tg) => tg.tasks).map((t) => ({ done: !!t.done })),
    })),
  )

  return (
    <div className="space-y-6">
      {milestones.map((m, i) => {
        const tasks = m.targets.flatMap((tg) => tg.tasks)
        const doneCount = tasks.filter((t) => t.done).length
        const state = states[i]
        if (tracked && state === 'upcoming' && !reopened.has(m.key)) {
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => toggle(m.key, true)}
              className="grid w-full cursor-pointer gap-1.5 rounded-xl border border-border/60 px-4 py-3 text-left transition-colors hover:border-border"
            >
              <span className="flex items-center justify-between gap-2">
                <MilestoneEyebrow index={i} count={milestones.length} />
                <MilestonePill state={state} />
              </span>
              <span className="truncate text-[15px] font-extrabold text-muted-foreground">
                {m.title}
              </span>
              <MilestoneSummary targets={m.targets.length} tasks={tasks.length} />
            </button>
          )
        }
        if (tracked && state === 'done' && !reopened.has(m.key)) {
          return (
            <FoldedMilestone
              key={m.key}
              index={i}
              count={milestones.length}
              title={m.title}
              done={doneCount}
              total={tasks.length}
              onClick={() => toggle(m.key, true)}
            />
          )
        }
        return (
          <PlanMilestone
            key={m.key}
            milestone={m}
            index={i}
            count={milestones.length}
            state={tracked ? state : undefined}
            sticky={sticky}
            onFold={tracked && state !== 'current' ? () => toggle(m.key, false) : undefined}
          />
        )
      })}
    </div>
  )
}

function PlanMilestone({
  milestone,
  index,
  count,
  state,
  sticky,
  onFold,
}: {
  milestone: OutlineMilestone
  index: number
  count: number
  state?: MilestoneState
  sticky: boolean
  onFold?: () => void
}) {
  const { t } = useTranslation()
  const tasks = milestone.targets.flatMap((tg) => tg.tasks)
  const doneCount = tasks.filter((tk) => tk.done).length
  const showLabels = milestone.targets.length > 1
  const headerRef = useRef<HTMLDivElement>(null)
  useStickyHeightVar(headerRef, '--ms-hdr-h', parentOf, sticky)
  const stuck = useStuck(headerRef, sticky) && sticky
  return (
    <section>
      <div
        ref={headerRef}
        className={cn(
          'grid gap-1.5',
          sticky &&
            'sticky top-(--app-hdr-h) z-20 -mx-4 border-b border-transparent bg-background px-4 py-2',
          stuck && 'border-border/60',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <MilestoneEyebrow index={index} count={count} />
          {state ? (
            <MilestonePill state={state} />
          ) : (
            <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/60">
              {t('templates.taskCount', { count: tasks.length })}
            </span>
          )}
        </div>
        <h3
          className={cn(
            'text-lg font-extrabold leading-snug',
            state === 'upcoming' && 'text-muted-foreground',
          )}
        >
          {milestone.title}
        </h3>
        {state && tasks.length > 0 && <MilestoneProgress done={doneCount} total={tasks.length} />}
      </div>
      <div className="mt-1">
        {milestone.targets.map((tg) => (
          <OutlineTargetBlock
            key={tg.key}
            target={tg}
            showLabel={showLabels}
            tracked={!!state}
            sticky={sticky}
          />
        ))}
      </div>
      {onFold && (
        <button
          type="button"
          onClick={onFold}
          className="mt-2 cursor-pointer text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t('goals.foldMilestone', { defaultValue: 'Fold milestone' })}
        </button>
      )}
    </section>
  )
}

const parentOf = (el: HTMLElement) => el.parentElement

function MilestoneSummary({ targets, tasks }: { targets: number; tasks: number }) {
  const { t } = useTranslation()
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
      <ChevronRight className="size-3.5" />
      {[
        targets > 1 ? t('goals.targetTotal', { count: targets }) : null,
        t('goals.taskTotal', { count: tasks }),
      ]
        .filter(Boolean)
        .join(' · ')}
    </span>
  )
}

// A target's tasks. When tracked, a finished target collapses to its heading and
// finished tasks in a partly done one fold into "N done" (owner tree parity).
function OutlineTargetBlock({
  target,
  showLabel,
  tracked,
  sticky,
}: {
  target: OutlineTarget
  showLabel: boolean
  tracked: boolean
  sticky: boolean
}) {
  const { t } = useTranslation()
  const done = target.tasks.filter((tk) => tk.done)
  const open = target.tasks.filter((tk) => !tk.done)
  const complete = tracked && target.tasks.length > 0 && open.length === 0
  const [expanded, setExpanded] = useState(!complete)
  const [showDone, setShowDone] = useState(false)
  const fold = tracked && !showDone && done.length > 0 && open.length > 0
  const visible = fold ? open : target.tasks
  const headerRef = useRef<HTMLButtonElement>(null)
  const stuck = useStuck(headerRef, sticky) && sticky
  return (
    <div>
      {showLabel &&
        (tracked ? (
          <button
            ref={headerRef}
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              '-mx-2 mt-2 flex w-[calc(100%+1rem)] cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/40',
              sticky &&
                'sticky top-[calc(var(--app-hdr-h)+var(--ms-hdr-h,0px))] z-10 border-b border-transparent bg-background hover:bg-[color-mix(in_oklab,var(--muted)_40%,var(--background))]',
              stuck && 'rounded-none border-border/60',
            )}
          >
            <ChevronRight
              className={cn(
                'size-3.5 shrink-0 text-muted-foreground/50 transition-transform',
                expanded && 'rotate-90',
              )}
            />
            {complete && <Check className="size-3.5 shrink-0 text-primary-text" strokeWidth={3} />}
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-widest',
                complete ? 'text-muted-foreground/60' : 'text-muted-foreground',
              )}
            >
              {target.title}
            </span>
            <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/60">
              {done.length}/{target.tasks.length}
            </span>
          </button>
        ) : (
          <TargetHeading title={target.title} count={String(target.tasks.length)} />
        ))}
      {(!showLabel || !tracked || expanded) && (
        <>
          {tracked && done.length > 0 && open.length > 0 && (
            <button
              type="button"
              onClick={() => setShowDone((v) => !v)}
              className="flex w-full cursor-pointer items-center gap-2 border-b border-border/30 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <Check className="size-3.5 text-primary-text" strokeWidth={3} />
              {showDone ? t('goals.hideDone') : t('goals.doneCount', { count: done.length })}
            </button>
          )}
          <ul>
            {visible.map((task) => (
              <PlanTaskRow
                key={task.key}
                wrap
                done={tracked ? !!task.done : undefined}
                title={task.title}
                meta={task.effort || task.minutes ? taskTimeLabel(task, '') : null}
                detail={task.description && <TaskDescription text={task.description} />}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function TaskDescription({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className={cn(
        'mt-0.5 block w-full cursor-pointer whitespace-pre-line break-words text-left text-xs leading-relaxed text-muted-foreground',
        !open && 'line-clamp-2',
      )}
    >
      <LinkifiedText text={text} />
    </button>
  )
}
